import type { ReactNode } from 'react';
import { useState } from 'react';
import { useFocus, useFocusVisible, useHover } from 'react-aria';
import { useLineClamp } from '@gonfalon/dom/useLineClamp';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import type { TooltipProps } from '@launchpad-ui/components';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { clsx } from 'clsx';

import styles from './TextTruncatorWithTooltip.module.css';

export interface TextTruncatorWithTooltipProps {
  /** The content to display and potentially truncate */
  children: ReactNode;
  /** The tooltip content to show on overflow. If not provided, uses children as tooltip content */
  tooltipContent?: ReactNode;
  /** Tooltip placement */
  tooltipPlacement?: TooltipProps['placement'];
  /** Additional CSS class name for the tooltip */
  tooltipClassName?: string;
  /** Additional CSS class name for the truncated content wrapper */
  className?: string;
  /** Maximum number of lines before truncating (defaults to 1 for single-line truncation) */
  maxLines?: number;
}

/**
 * A component that wraps content with a tooltip that only appears when the content overflows.
 * Handles all the complexity of overflow detection, focus management, and accessibility.
 *
 * @example
 * ```tsx
 * <TextTruncatorWithTooltip className={styles.title}>
 *   <h3>This is a very long title that might overflow</h3>
 * </TextTruncatorWithTooltip>
 * ```
 *
 * @example
 * ```tsx
 * <TextTruncatorWithTooltip tooltipContent="Custom tooltip text">
 *   <span>Truncated text</span>
 * </TextTruncatorWithTooltip>
 * ```
 *
 * @example
 * ```tsx
 * <TextTruncatorWithTooltip maxLines={2}>
 *   <span>This text can wrap to 2 lines before truncating</span>
 * </TextTruncatorWithTooltip>
 * ```
 */
export function TextTruncatorWithTooltip({
  children,
  tooltipContent,
  tooltipPlacement = 'bottom',
  tooltipClassName,
  className,
  maxLines = 1,
}: TextTruncatorWithTooltipProps) {
  // Use appropriate hook based on maxLines
  const singleLine = useTextOverflow<HTMLSpanElement>();
  const multiLine = useLineClamp<HTMLSpanElement>({ lines: maxLines });
  const { ref, hasOverflow, props: overflowProps } = maxLines === 1 ? singleLine : multiLine;
  const style = maxLines === 1 ? undefined : multiLine.props.style;

  const { hoverProps, isHovered } = useHover({});
  const [isFocused, setFocused] = useState(false);
  const { focusProps } = useFocus({
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });
  const { isFocusVisible } = useFocusVisible();

  return (
    <TooltipTrigger isDisabled={!hasOverflow} isOpen={hasOverflow && (isHovered || (isFocusVisible && isFocused))}>
      <Focusable>
        <span
          {...focusProps}
          {...hoverProps}
          ref={ref}
          role="button"
          style={style}
          className={clsx(
            styles.wrapper,
            maxLines === 1 ? styles.singleLine : styles.multiLine,
            className,
            overflowProps.className,
          )}
        >
          {children}
        </span>
      </Focusable>
      <Tooltip placement={tooltipPlacement} className={clsx(styles.tooltip, tooltipClassName)}>
        {tooltipContent ?? children}
      </Tooltip>
    </TooltipTrigger>
  );
}
